
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from './core/services/auth.service';
import { AuthSyncService } from './core/services/auth-sync.service';
import { ToastComponent } from './shared/components/toast.component';

declare const CobrowseIO: any;

@Component({
  selector: 'app-root',
  imports: [ToastComponent, RouterOutlet],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private authSyncService = inject(AuthSyncService);

  private authSubscription?: Subscription;
  private cobrowseUpdateVersion = 0;

  public isIframe: boolean = window !== window.parent && !window.opener;

  ngOnInit(): void {
    this.authSyncService.start();

    this.authSubscription = this.authService.isAuthenticated$.subscribe(
      isAuthenticated => {
        const updateVersion = ++this.cobrowseUpdateVersion;

        if (isAuthenticated) {
          void this.updateCobrowseIdentity(updateVersion);
        } else if (typeof CobrowseIO !== 'undefined') {
          CobrowseIO.customData = {
            user_id: '',
            user_email: '',
            user_name: '',
            is_impersonating: 'No',
            impersonated_email: '',
            impersonated_user_id: ''
          };
        }
      }
    );
  }

  private async updateCobrowseIdentity(updateVersion: number): Promise<void> {
    const authUser = await this.authService.getAuthUser();

    if (authUser === null || typeof CobrowseIO === 'undefined') {
      return;
    }

    const isImpersonating = authUser.isImpersonating;

    const cognitoUserData = isImpersonating
      ? await this.authService.getCognitoUserData()
      : null;

    if (updateVersion !== this.cobrowseUpdateVersion) {
      return;
    }

    const employeeEmail = cognitoUserData?.email;

    const employeeName = [
      cognitoUserData?.given_name,
      cognitoUserData?.family_name
    ].filter(Boolean).join(' ');

    CobrowseIO.customData = {
      user_id: isImpersonating
        ? authUser.authUserId
        : authUser.userId,
      user_email: isImpersonating
        ? employeeEmail
          ? `${employeeEmail} (Impersonating)`
          : 'Employee (Impersonating)'
        : authUser.email,
      user_name: isImpersonating
        ? employeeName || employeeEmail || 'Employee'
        : `${authUser.firstName} ${authUser.lastName}`,
      is_impersonating: isImpersonating ? 'Yes' : 'No',
      impersonated_email: isImpersonating
        ? authUser.impersonatedDisplayName ?? ''
        : '',

      impersonated_user_id: isImpersonating
        ? authUser.userId
        : ''
    };

    console.log('CoBrowse identity updated:', CobrowseIO.customData);
  }

  ngOnDestroy(): void {
    ++this.cobrowseUpdateVersion;
    this.authSubscription?.unsubscribe();
  }
}
